/**
 * emailGuard - Blocco email non recapitabili / usa-e-getta / generate da bot
 *
 * Usato in registrazione (client + AuthProvider) e nel pannello admin
 * per evidenziare account sospetti.
 */

/** Domini non recapitabili o riservati ai test (RFC 2606 + varianti) */
export const UNDELIVERABLE_DOMAINS = new Set<string>([
  'example.com', 'example.org', 'example.net', 'example.edu', 'example',
  'test.com', 'test.org', 'test', 'invalid', 'localhost', 'local',
  'domain.com', 'email.com', 'mail.com', 'esempio.it', 'esempio.com',
  'yourdomain.com', 'mydomain.com', 'sito.it', 'dominio.it',
]);

/** Domini di email temporanee/usa-e-getta */
export const DISPOSABLE_DOMAIN_TOKENS = [
  'mailinator', 'yopmail', '10minutemail', '10minutmail', 'guerrillamail',
  'guerrilla', 'tempmail', 'temp-mail', 'tempail', 'throwaway', 'trashmail',
  'sharklasers', 'grr.la', 'dispostable', 'getnada', 'nada.email',
  'mohmal', 'fakeinbox', 'maildrop', 'mailnesia', 'mintemail', 'moakt',
  'emailondeck', 'spamgourmet', 'anonbox', 'discard.email', 'inboxbear',
  'mailcatch', 'mytemp', 'temporary-mail', 'burnermail', 'tmpmail',
];

/**
 * Pattern nella parte locale tipici di script/agent di test.
 * Volutamente stretti per non colpire persone reali (es. "battista", "roberto").
 */
export const BOT_LOCAL_PATTERNS: RegExp[] = [
  /^agent[-._]?test/i,
  /^test[-._]?agent/i,
  /^agent\d+$/i,
  /^bot[-._]?\d*$/i,
  /^[a-z]*bot\d+$/i,
  /^qa[-._]?test/i,
  /^test[-._]?\d{2,}$/i,
  /^test[-._]?user/i,
  /^user[-._]?test\d*$/i,
  /^automation/i,
  /^selenium/i,
  /^playwright/i,
  /^puppeteer/i,
  /^e2e[-._]?/i,
  /^dummy[-._]?\d*$/i,
  /^fake[-._]?(user|mail|email)/i,
  /^noreply$/i,
  /^no-reply$/i,
];

export type EmailGuardReason =
  | 'invalid'
  | 'undeliverable_domain'
  | 'disposable_domain'
  | 'bot_pattern';

export interface EmailGuardResult {
  allowed: boolean;
  reason?: EmailGuardReason;
  /** Messaggio pronto da mostrare all'utente (italiano) */
  message?: string;
}

const BASIC_EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

const MESSAGES: Record<EmailGuardReason, string> = {
  invalid: 'Indirizzo email non valido.',
  undeliverable_domain:
    'Questo dominio email non è utilizzabile per la registrazione. Inserisci un indirizzo email reale.',
  disposable_domain:
    'Non accettiamo indirizzi email temporanei. Usa un indirizzo email personale valido.',
  bot_pattern:
    'Questo indirizzo email non è utilizzabile per la registrazione. Inserisci un indirizzo email personale.',
};

/** Estrae [localPart, domain] normalizzati */
export function splitEmail(email: string): [string, string] {
  const lower = String(email || '').trim().toLowerCase();
  const at = lower.lastIndexOf('@');
  if (at <= 0) return [lower, ''];
  return [lower.slice(0, at), lower.slice(at + 1).replace(/^www\./, '')];
}

/** true se il dominio non è recapitabile (esempio/test/riservato) */
export function isUndeliverableDomain(domain: string, extraDomains: string[] = []): boolean {
  if (!domain) return true;
  if (UNDELIVERABLE_DOMAINS.has(domain)) return true;
  if (extraDomains.some((d) => d && domain === d.toLowerCase())) return true;
  const tld = domain.split('.').pop() || '';
  return ['test', 'invalid', 'localhost', 'example', 'local'].includes(tld);
}

/** true se il dominio è di un servizio di email temporanee */
export function isDisposableDomain(domain: string, extraTokens: string[] = []): boolean {
  if (!domain) return false;
  return [...DISPOSABLE_DOMAIN_TOKENS, ...extraTokens]
    .filter(Boolean)
    .some((t) => domain.includes(t.toLowerCase()));
}

/** Compila i pattern extra scartando le regex non valide */
function compileExtraPatterns(patterns: string[]): RegExp[] {
  const out: RegExp[] = [];
  for (const p of patterns) {
    if (!p) continue;
    try {
      out.push(new RegExp(p, 'i'));
    } catch {
      // pattern non valido: ignorato (già validato in fase di salvataggio admin)
    }
  }
  return out;
}

/** true se la parte locale sembra generata da uno script/agent */
export function isBotLocalPart(localPart: string, extraPatterns: string[] = []): boolean {
  if (!localPart) return false;
  const normalized = localPart.replace(/\+.*$/, '');
  const all = [...BOT_LOCAL_PATTERNS, ...compileExtraPatterns(extraPatterns)];
  return all.some((re) => re.test(normalized));
}

/**
 * Criteri email configurabili dal pannello admin.
 * Se assenti, si applicano i controlli di default (tutti attivi, liste base).
 */
export interface EmailGuardOptions {
  block_undeliverable_domains?: boolean;
  block_disposable_domains?: boolean;
  block_bot_patterns?: boolean;
  extra_blocked_domains?: string[];
  extra_disposable_tokens?: string[];
  extra_bot_patterns?: string[];
}

/**
 * Valuta se una email è accettabile per la registrazione.
 * Non fa chiamate di rete: puro controllo sintattico/lista.
 */
export function checkSignupEmail(
  email: string,
  options?: EmailGuardOptions
): EmailGuardResult {
  const raw = String(email || '').trim();
  if (!BASIC_EMAIL_RE.test(raw)) {
    return { allowed: false, reason: 'invalid', message: MESSAGES.invalid };
  }

  const opts = options || {};
  const checkUndeliverable = opts.block_undeliverable_domains !== false;
  const checkDisposable = opts.block_disposable_domains !== false;
  const checkBot = opts.block_bot_patterns !== false;

  const [localPart, domain] = splitEmail(raw);

  if (checkUndeliverable && isUndeliverableDomain(domain, opts.extra_blocked_domains)) {
    return {
      allowed: false,
      reason: 'undeliverable_domain',
      message: MESSAGES.undeliverable_domain,
    };
  }
  if (checkDisposable && isDisposableDomain(domain, opts.extra_disposable_tokens)) {
    return {
      allowed: false,
      reason: 'disposable_domain',
      message: MESSAGES.disposable_domain,
    };
  }
  if (checkBot && isBotLocalPart(localPart, opts.extra_bot_patterns)) {
    return { allowed: false, reason: 'bot_pattern', message: MESSAGES.bot_pattern };
  }

  return { allowed: true };
}

/** Helper booleano per UI/admin: true se l'email appare automatica o non recapitabile */
export function isSuspiciousEmail(email: string, options?: EmailGuardOptions): boolean {
  return !checkSignupEmail(email, options).allowed;
}

